import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, Info, ArrowUpRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { reopenPlannedBatch } from '../../../ledger/plans';
import { initMissingBalances } from '../../../ledger/maintenance';
import { useData } from '../../store/data';
import { confirmAsk } from '../../components/confirm';
import Pill from '../../components/kit/Pill';
import { checkIntegrity, type IntegrityIssue } from './integrity';

const TONE = { bad: 'bad', warn: 'warn', info: 'neutral' } as const;
const LABEL = { bad: 'Cần xử lý', warn: 'Nên xem', info: 'Chỉ để biết' } as const;

export default function IntegrityTab() {
  const d = useData(s => s.data);
  const issues = useMemo(() => checkIntegrity(d as any), [d]);
  const [busy, setBusy] = useState<string | null>(null);

  const fix = async (i: IntegrityIssue) => {
    if (!i.fix) return;
    const f = i.fix;
    const reopen = f.kind === 'reopen';
    if (!await confirmAsk({
      title: f.label + '?', confirmLabel: reopen ? 'Mở lại' : 'Đặt số dư 0', destructive: false,
      description: reopen ? 'Các khoản này trở lại trạng thái chờ xác nhận ở mục Dự kiến. Không có giao dịch hay số dư nào bị thay đổi.' : 'Chỉ thêm trường số dư = 0 (bằng đúng giá trị đang được tính). Không con số nào thay đổi.'
    })) return;
    setBusy(i.id);
    const uid = getAuth().currentUser!.uid;
    const r = f.kind === 'reopen' ? await reopenPlannedBatch(uid, f.collection, f.ids) : await initMissingBalances(uid, f.ids);
    setBusy(null);
    r.success ? toast.success(reopen ? `Đã mở lại ${r.count} khoản dự kiến` : `Đã sửa ${r.count} ví`) : toast.error(r.error ?? 'Không xử lý được');
  };

  return (
    <div className="space-y-4">
      <p className="max-w-prose text-muted-foreground">Rà soát nhanh dữ liệu đã tải: liên kết giữa dự kiến và giao dịch, ví bất thường, mẫu định kỳ. Không tự sửa gì; mỗi mục nói rõ cách xử lý. Kiểm tra này không tốn thêm lượt đọc Firebase.</p>
      {issues.length === 0 ? (
        <Card className="flex items-center gap-3 p-5"><CheckCircle2 className="text-good" aria-hidden /><div><p className="font-medium">Mọi thứ đều khớp</p><p className="text-sm text-muted-foreground">Không phát hiện liên kết mất, ví bất thường hay mẫu trùng.</p></div></Card>
      ) : issues.map(i => (
        <Card key={i.id} className={cn('space-y-3 p-4', i.severity === 'bad' && 'border-bad/40')}>
          <div className="flex items-start gap-3">
            {i.severity === 'info' ? <Info size={18} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden /> : <AlertTriangle size={18} className={cn('mt-0.5 shrink-0', i.severity === 'bad' ? 'text-bad' : 'text-warn')} aria-hidden />}
            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-medium"><Pill tone={TONE[i.severity]} className="mr-2">{LABEL[i.severity]}</Pill>{i.title}</p>
              <p className="text-sm text-muted-foreground">{i.detail}</p>
            </div>
          </div>
          {i.items.length > 0 && <ul className="num space-y-0.5 rounded-md bg-secondary px-3 py-2 text-sm">{i.items.map((t, k) => <li key={k} className="truncate">{t}</li>)}</ul>}
          <div className="flex flex-wrap gap-2">
            {i.fix && <Button size="sm" onClick={() => fix(i)} disabled={busy === i.id}>{busy === i.id ? 'Đang xử lý…' : i.fix.label}</Button>}
            {i.link && <Link to={i.link.to} className="inline-flex min-h-9 items-center gap-1 text-sm font-medium text-primary hover:underline">{i.link.label} <ArrowUpRight size={14} aria-hidden /></Link>}
          </div>
        </Card>
      ))}
    </div>
  );
}
